import {
  Flex,
  MultiSelectInput,
  SecondaryButton,
  SelectInput,
  SubHeaderContainerProps,
  useCombinedUser,
  useStyledTheme,
} from '@equifinance/shared';
import styled, { css } from 'styled-components';
import {
  useCallback, useEffect, useMemo, useRef,
} from 'react';
import { UnderwritingStatuses, useApplicationsContext } from '../../../contexts/applications';
import { useApplicationsUIContext } from '../../../contexts/applications/ApplicationsUIProvider';
import { useCacheFilters } from '../../../hooks/useCacheFilters';

const statusFilterCss = css` ${({ theme }) => `
  color: ${theme.colours.neutral[2]};
  .dropdown-container, .multi-select-parent {
    height: ${theme.padding[8]};
    font-size: ${theme.fontSize[1]};
    font-weight: ${theme.fontWeight.bold};
  }
  .multi-select-parent {
    min-width: auto;
  }
  .dropdown-container {
    border-radius: ${theme.padding[2]};
    border: 1px solid ${theme.colours.neutral[6]};
  }
  .mi-chevron-up, .mi-chevron-down {
    font-size: ${theme.fontSize[3]};
  }
  .dropdown-content {
    min-width: ${theme.dropdownWidth};
  }
  .mi-close {
    font-size: ${theme.fontSize[3]};
    display: flex;
  }
`}`;

const selectCss = css` ${({ theme }) => `
  font-size: ${theme.fontSize[1]};
  border-radius: ${theme.borderRadius[2]};
  font-weight: ${theme.fontWeight.bold};

  &[disabled] {
    color: ${theme.colours.neutral[5]};
    background: transparent;
    border: ${theme.borderWidth[1]} solid ${theme.colours.neutral[7]};
  }
`}`;

const ApplicationsFilters = styled(Flex)` ${({ theme }) => `
  gap: ${theme.padding[2]};
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  padding: ${theme.padding[4]} 0;
  background: ${theme.colours.neutral[10]};
  position: sticky;
  top: ${theme.headerHeight};
  z-index: 4;
`}`;

const SelectApplicationContainer = styled(Flex)` ${({ theme }) => `
  gap: ${theme.padding[2]};
  align-items: center;
  justify-content: center;
`}`;

const SelectApplicationButton = styled(SecondaryButton) <{ selected: boolean }>`${({ theme, selected }) => `
  background: ${selected ? theme.colours.primary[11] : theme.colours.neutral[10]};
  color: ${selected ? theme.colours.primary[1] : theme.colours.neutral[2]};
  border-color: ${selected ? theme.colours.primary[11] : theme.colours.neutral[6]};
  height: ${theme.padding[8]};
  font-weight: ${theme.fontWeight.bold};
  padding: ${theme.padding[2]} ${theme.padding[4]};
  flex-shrink: 0;
`}`;

export enum ApplicationTypeOptions {
  ALL = 'All applications',
  MINE = 'My applications',
  UNASSIGNED = 'Unassigned applications',
}

type InternalLandingPageFilterSettings = {
  onlyUpdatedApplications: boolean,
  statusFilter: string[];
  typeFilter: keyof typeof ApplicationTypeOptions
}

const defaultFilters: InternalLandingPageFilterSettings = {
  onlyUpdatedApplications: false,
  statusFilter: [],
  typeFilter: 'ALL',
};

export default function ApplicationsFilter({ searchComponent }: SubHeaderContainerProps) {
  const theme = useStyledTheme();
  const { applications } = useApplicationsContext();
  const ref = useRef<HTMLDivElement>(null);
  const { user } = useCombinedUser();

  const {
    filterStatus,
    filterType,
    onlyUpdatedApplications,
    setFilterStatus,
    setFilterType,
    setOnlyUpdatedApplications,
    setFilterSectionHeight,
  } = useApplicationsUIContext();

  const { filters, updateFilters } = useCacheFilters<InternalLandingPageFilterSettings>({
    pageKey: 'internal-portal-landing-page',
    userId: user?.id || '',
    defaultFilters,
  });

  // Restore Cached filters settings
  useEffect(() => {
    const { onlyUpdatedApplications, statusFilter, typeFilter } = filters;
    setOnlyUpdatedApplications(onlyUpdatedApplications);
    setFilterStatus(statusFilter);
    setFilterType(typeFilter);
  }, []);

  const handleResize = useCallback(() => {
    if (!ref?.current) return;
    setFilterSectionHeight(ref.current.clientHeight);
  }, []);

  const resizeObs = useMemo(() => new ResizeObserver(handleResize), [handleResize]);

  useEffect(() => {
    let domNode;

    if (ref?.current) domNode = ref.current;

    if (domNode) resizeObs.observe(domNode);

    return () => resizeObs.disconnect();
  }, [ref, resizeObs]);

  return (
    <ApplicationsFilters ref={ref}>
      {searchComponent}
      <SelectApplicationContainer>
        <SelectInput
          small
          id="application-type-filter"
          disabled={!applications.length}
          options={ApplicationTypeOptions}
          value={filterType}
          dropdownCss={selectCss}
          onChange={(value) => {
            const newValue = value as keyof typeof ApplicationTypeOptions;
            setFilterType(newValue);
            updateFilters({ typeFilter: newValue });
          }}
        />
        <SelectApplicationButton
          small
          fontSize={theme.fontSize[1]}
          selected={onlyUpdatedApplications}
          disabled={!applications.length}
          onClick={() => {
            setOnlyUpdatedApplications(!onlyUpdatedApplications);
            updateFilters({ onlyUpdatedApplications: !onlyUpdatedApplications });
          }}
        >
          Updated applications
        </SelectApplicationButton>
      </SelectApplicationContainer>
      <MultiSelectInput
        id="case-status-filter"
        onChange={(value: string[]) => {
          setFilterStatus(value);
          updateFilters({ statusFilter: value });
        }}
        value={filterStatus}
        selectedLabel={filterStatus.length ? 'Status:' : 'Status'}
        options={Object.values(UnderwritingStatuses).reduce((a, v) => ({ ...a, [v]: v }), {})}
        onDeselectOption={(value: string) => {
          const newValue = filterStatus.filter((id) => id !== value);
          setFilterStatus(newValue);
          updateFilters({ statusFilter: newValue });
        }}
        disabled={!applications.length}
        containerStyles={statusFilterCss}
      />
    </ApplicationsFilters>
  );
}
